import React from 'react';

/*
 * Harbour icon set, exported 1:1 from the Figma component library
 * ("SHARED · icons" and "SHARED · glyphs", file UAXOnhR1ZvlsNl4diy00xX).
 * Icons are 24px stroke drawings recoloured via currentColor; glyphs are the
 * 20px status shapes used inside status chips and save pills.
 */

const ICON_PATHS = {
  'arrow-left': 'M19 12H5M12 5L5 12L12 19',
  'arrow-right': 'M5 12H19M12 19L19 12L12 5',
  check: 'M20 6L9 17L4 12',
  'chevron-down': 'M6 9L12 15L18 9',
  'chevron-left': 'M15 6L9 12L15 18',
  'chevron-right': 'M9 6L15 12L9 18',
  clock: 'M12 7V12L15 14M21 12C21 16.971 16.971 21 12 21C7.029 21 3 16.971 3 12C3 7.029 7.029 3 12 3C16.971 3 21 7.029 21 12Z',
  close: 'M18 6L6 18M6 6L18 18',
  download: 'M12 4V14.5M17 9.5L12 14.5L7 9.5M4 15V18C4 19.105 4.895 20 6 20H18C19.105 20 20 19.105 20 18V15',
  edit: 'M13.5 6.5L17.5 10.5M4 20V16L15.6 4.4C16.4 3.6 17.6 3.6 18.4 4.4L19.6 5.6C20.4 6.4 20.4 7.6 19.6 8.4L8 20H4Z',
  email: 'M3.5 7.5L12 13L20.5 7.5M5 5H19C20.105 5 21 5.895 21 7V17C21 18.105 20.105 19 19 19H5C3.895 19 3 18.105 3 17V7C3 5.895 3.895 5 5 5Z',
  error: 'M15 9L9 15M9 9L15 15M21 12C21 16.971 16.971 21 12 21C7.029 21 3 16.971 3 12C3 7.029 7.029 3 12 3C16.971 3 21 7.029 21 12Z',
  warning: 'M12 7.5V13M12 16.25V16.75M21 12C21 16.971 16.971 21 12 21C7.029 21 3 16.971 3 12C3 7.029 7.029 3 12 3C16.971 3 21 7.029 21 12Z',
  eye: 'M2.5 12C4.8 7.6 8.1 5 12 5C15.9 5 19.2 7.6 21.5 12C19.2 16.4 15.9 19 12 19C8.1 19 4.8 16.4 2.5 12ZM15 12C15 13.657 13.657 15 12 15C10.343 15 9 13.657 9 12C9 10.343 10.343 9 12 9C13.657 9 15 10.343 15 12Z',
  // A plain document-with-fold mark for PDF rows. Deliberately NOT the design
  // file's composite asset, which embeds the Adobe Acrobat trademark.
  'file-pdf': 'M14 3H7C5.895 3 5 3.895 5 5V19C5 20.105 5.895 21 7 21H17C18.105 21 19 20.105 19 19V8M14 3L19 8M14 3V8H19M9 13H15M9 17H13',
  filter: 'M4 6H20M7 12H17M10 18H14',
  help: 'M9.2 9.2C9.5 7.8 10.6 7 12 7C13.66 7 15 8.1 15 9.6C15 10.9 14.1 11.5 13.1 12C12.4 12.35 12 12.9 12 13.6V14M12 16.75V17.25M21 12C21 16.971 16.971 21 12 21C7.029 21 3 16.971 3 12C3 7.029 7.029 3 12 3C16.971 3 21 7.029 21 12Z',
  info: 'M12 11V16M12 7.75V8.25M21 12C21 16.971 16.971 21 12 21C7.029 21 3 16.971 3 12C3 7.029 7.029 3 12 3C16.971 3 21 7.029 21 12Z',
  lock: 'M8 11V7.5C8 5.291 9.791 3.5 12 3.5C14.209 3.5 16 5.291 16 7.5V11M12 15V17M7 11H17C18.105 11 19 11.895 19 13V19C19 20.105 18.105 21 17 21H7C5.895 21 5 20.105 5 19V13C5 11.895 5.895 11 7 11Z',
  phone: 'M20.5 16.3V19C20.5 20 19.7 20.8 18.7 20.7C10.6 20.1 3.89999 13.4 3.29999 5.3C3.19999 4.3 3.99999 3.5 4.99999 3.5H7.69999C8.59999 3.5 9.29999 4.1 9.49999 5L9.99999 7.6C10.1 8.2 9.89999 8.8 9.49999 9.2L8.19999 10.5C9.29999 12.7 11.3 14.7 13.5 15.8L14.8 14.5C15.2 14.1 15.8 13.9 16.4 14L19 14.5C19.9 14.7 20.5 15.4 20.5 16.3Z',
  plus: 'M12 5V19M5 12H19',
  user: 'M4.5 20.5C4.5 16.6 7.9 14 12 14C16.1 14 19.5 16.6 19.5 20.5M16 8C16 10.209 14.209 12 12 12C9.791 12 8 10.209 8 8C8 5.791 9.791 4 12 4C14.209 4 16 5.791 16 8Z',
};

export const HARBOUR_ICON_NAMES = Object.keys(ICON_PATHS).concat(['check-circle', 'spinner']);

export default function HarbourIcon({ name, size = 20, title, className = '' }) {
  const a11y = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true };

  if (name === 'check-circle') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...a11y}>
        <path
          d="M12 7.75V8.25M15.5 10L11 14.5L8.5 12M21 12C21 16.971 16.971 21 12 21C7.029 21 3 16.971 3 12C3 7.029 7.029 3 12 3C16.971 3 21 7.029 21 12Z"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    );
  }

  if (name === 'spinner') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={`animate-spin ${className}`} {...a11y}>
        <circle opacity="0.2" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path
          d="M12 3C13.1819 3 14.3522 3.23279 15.4442 3.68508C16.5361 4.13738 17.5282 4.80031 18.364 5.63604C19.1997 6.47177 19.8626 7.46392 20.3149 8.55585C20.7672 9.64778 21 10.8181 21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  const d = ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...a11y}>
      <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/*
 * Status glyphs (20px). Ring glyphs recolour via currentColor; Alert and Stop
 * keep their fixed semantic fills with white detail, per the library.
 */
export function HarbourGlyph({ kind, size = 16, className = '' }) {
  const common = { width: size, height: size, viewBox: '0 0 20 20', fill: 'none', 'aria-hidden': true, className };
  const ring = (
    <path
      d="M17.5 10C17.5 14.1422 14.1422 17.5 10 17.5C5.85775 17.5 2.5 14.1422 2.5 10C2.5 5.85775 5.85775 2.5 10 2.5C14.1422 2.5 17.5 5.85775 17.5 10Z"
      stroke="currentColor"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
  switch (kind) {
    case 'ring-empty':
      return <svg {...common}>{ring}</svg>;
    case 'ring-half':
      return (
        <svg {...common}>
          {ring}
          <path d="M10 1.66667C14.6025 1.66667 18.3333 5.39751 18.3333 10C18.3333 14.6025 14.6025 18.3333 10 18.3333V1.66667Z" fill="currentColor" />
        </svg>
      );
    case 'ring-tick':
      return (
        <svg {...common}>
          {ring}
          <path d="M6.25 10.4167L8.75 12.9167L13.75 7.5" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'alert':
      return (
        <svg {...common}>
          <circle cx="10" cy="10" r="10" fill="#7A4F00" />
          <path d="M10 5.41667V11.4167" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10 14.1667V14.1767" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'stop':
      return (
        <svg {...common}>
          <path
            d="M6.66675 1.66667H13.3334L18.3334 6.66667V13.3333L13.3334 18.3333H6.66675L1.66675 13.3333V6.66667L6.66675 1.66667Z"
            fill="#A93226"
            stroke="#A93226"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M6.25 10H15.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...common}>
          <path
            d="M10 5.83333V10L12.5 11.6667M17.5 10C17.5 14.1425 14.1425 17.5 10 17.5C5.8575 17.5 2.5 14.1425 2.5 10C2.5 5.8575 5.8575 2.5 10 2.5C14.1425 2.5 17.5 5.8575 17.5 10Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'eye':
      return (
        <svg {...common}>
          <path
            d="M2.08325 10C3.99992 6.33334 6.74992 4.16667 9.99992 4.16667C13.2499 4.16667 15.9999 6.33334 17.9166 10C15.9999 13.6667 13.2499 15.8333 9.99992 15.8333C6.74992 15.8333 3.99992 13.6667 2.08325 10ZM12.4999 10C12.4999 11.3808 11.3808 12.5 9.99992 12.5C8.61909 12.5 7.49992 11.3808 7.49992 10C7.49992 8.61917 8.61909 7.50001 9.99992 7.50001C11.3808 7.50001 12.4999 8.61917 12.4999 10Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
}
